import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstMediaUrl, mediaUrl } from "lib/media";
import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import {
  normalizeSpecEntries,
  normalizeTechnicalSections,
  normalizeTransmissionNames,
  productStoryText,
} from "storefronts/shared/pimProductContent";
import StorefrontLayout from "../components/StorefrontLayout";
import VbsIcon from "../components/VbsIcon";
import {
  VBS,
  priceText,
  resolvePrice,
  btnPrimary,
  btnSecondary,
  resetButton,
  labelCaps,
} from "../theme";

function normalizeProduct(data) {
  if (!data?.data) return null;
  return data.data.attributes
    ? {
        id: data.data.id,
        documentId: data.data.documentId || data.data.id,
        ...data.data.attributes,
      }
    : { ...data.data, documentId: data.data.documentId || data.data.id };
}

function HtmlOrText({ value }) {
  if (!value) return null;
  if (String(value).includes("<")) {
    return (
      <Box
        sx={{
          color: VBS.muted,
          lineHeight: 1.7,
          fontSize: "0.9375rem",
          "& p": { m: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return (
    <Box
      component="p"
      sx={{ m: 0, color: VBS.muted, lineHeight: 1.7, fontSize: "0.9375rem", whiteSpace: "pre-wrap" }}
    >
      {value}
    </Box>
  );
}

HtmlOrText.propTypes = {
  value: PropTypes.string,
};

HtmlOrText.defaultProps = {
  value: "",
};

export default function ProductDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, lines } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const { t } = useStorefrontCopy();
  const canOrder = !!token;
  const quoteLabel = t("common.requestQuote", "Request quote");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [cartNotice, setCartNotice] = useState("");

  const isMobileRoute = location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const companyKey = String(storefrontTenant || activeCompany || "vbsus").toLowerCase();
  const textLocale = getTextLocale();

  const { data, isLoading, error } = useQuery({
    queryKey: ["vbsus-product", documentId, companyKey, textLocale, token ? "auth" : "public"],
    queryFn: () => get(`/api/storefront/catalog/${documentId}`),
    enabled: !!documentId,
    staleTime: 30000,
  });

  const currency = data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);
  const product = normalizeProduct(data);
  const effectivePrice = resolvePrice(product);
  const hasEffectivePrice = Number(effectivePrice) > 0;
  const coreCharge = Number(product?.coreCharge || 0);
  const images = Array.isArray(product?.images?.data)
    ? product.images.data
    : Array.isArray(product?.images)
      ? product.images
      : [];
  const selectedImage = mediaUrl(images[selectedImageIndex]) || null;
  const unitPriceNumber = Number(effectivePrice || 0);
  const lineTotal = hasEffectivePrice ? (unitPriceNumber + coreCharge) * orderQuantity : 0;
  const inCartQuantity =
    (lines || []).find((line) => String(line.documentId) === String(product?.documentId))?.qty ||
    0;
  const story = productStoryText(product);
  const unitDetails = String(product?.unitSpecificDetails || "").trim();
  const installationNotes = String(product?.installationNotes || "").trim();
  const fitmentYears = String(product?.fitmentYears || "").trim();
  const transmissions = normalizeTransmissionNames(product?.transmissions);
  const specEntries = normalizeSpecEntries(product?.specifications);
  const technicalSections = normalizeTechnicalSections(product?.technicalSections);

  const setQuantity = (value) => {
    setOrderQuantity(Math.max(1, Math.min(99, Math.floor(Number(value || 1) || 1))));
  };

  const addProductToCart = (goToCart = false) => {
    if (!canOrder) {
      navigate(
        toPath(`/authentication/sign-in?next=${encodeURIComponent(location.pathname)}`),
      );
      return;
    }
    if (!product || !hasEffectivePrice) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: unitPriceNumber,
        coreCharge: product.coreCharge != null ? coreCharge : null,
        imageUrl: firstMediaUrl(product?.images),
      },
      orderQuantity,
    );
    setCartNotice(
      t("product.addedNotice", "{count} added to cart").replace(
        "{count}",
        String(orderQuantity),
      ),
    );
    if (goToCart) navigate(toPath("/cart"));
  };

  if (isLoading) {
    return (
      <StorefrontLayout>
        <Box sx={{ py: 10, color: VBS.muted }}>{t("product.loadingProduct", "Loading product…")}</Box>
      </StorefrontLayout>
    );
  }

  if (error || !product) {
    return (
      <StorefrontLayout>
        <Box sx={{ py: 10, textAlign: "center" }}>
          <Box component="h1" sx={{ m: 0, fontWeight: 800, color: VBS.ink }}>
            {t("product.notFound", "Product not found")}
          </Box>
          <Box component="button" type="button" onClick={() => navigate(toPath("/catalog"))} sx={{ ...btnPrimary, mt: 2.5 }}>
            {t("product.backToCatalog", "Back to catalog")}
          </Box>
        </Box>
      </StorefrontLayout>
    );
  }

  const buyPanel = (
    <Box
      sx={{
        bgcolor: VBS.surface,
        border: `1px solid ${VBS.lineStrong}`,
        borderRadius: `${VBS.radiusMd}px`,
        borderTop: "none",
        boxShadow: VBS.shadowCard,
        p: 2.5,
        position: { xs: "relative", md: "sticky" },
        top: { md: 120 },
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: VBS.swoosh,
        },
      }}
    >
      <Box sx={{ ...labelCaps, mb: 0.5 }} component="p">
        {t("product.unitPrice", "Unit price")}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: "1.75rem",
          fontWeight: 800,
          fontFamily: VBS.fontMono,
          color: VBS.accent,
        }}
      >
        {priceText(effectivePrice, currency, quoteLabel)}
      </Box>
      {coreCharge > 0 ? (
        <Box component="p" sx={{ m: "0.35rem 0 0", fontSize: "0.8125rem", color: VBS.muted }}>
          + {priceText(coreCharge, currency, quoteLabel)}{" "}
          {t("product.coreDeposit", "core deposit (refundable)")}
        </Box>
      ) : null}

      {hasEffectivePrice ? (
        <Box sx={{ mt: 2.5 }}>
          {(inCartQuantity > 0 || cartNotice) && (
            <Box
              component="p"
              sx={{ m: "0 0 1rem", fontSize: "0.8125rem", color: VBS.accent, fontWeight: 600 }}
            >
              {cartNotice ||
                `${inCartQuantity} ${t("product.alreadyInCart", "already in cart")}`}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setQuantity(orderQuantity - 1)}
              sx={{
                ...resetButton,
                width: 36,
                height: 36,
                border: `1px solid ${VBS.lineStrong}`,
                borderRadius: `${VBS.radius}px`,
                display: "grid",
                placeItems: "center",
              }}
            >
              <VbsIcon name="remove" size={16} color={VBS.ink} />
            </Box>
            <Box
              component="span"
              sx={{
                minWidth: 32,
                textAlign: "center",
                fontWeight: 800,
                fontFamily: VBS.fontMono,
              }}
            >
              {orderQuantity}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setQuantity(orderQuantity + 1)}
              sx={{
                ...resetButton,
                width: 36,
                height: 36,
                border: `1px solid ${VBS.lineStrong}`,
                borderRadius: `${VBS.radius}px`,
                display: "grid",
                placeItems: "center",
              }}
            >
              <VbsIcon name="add" size={16} color={VBS.ink} />
            </Box>
            <Box sx={{ ml: "auto", fontFamily: VBS.fontMono, fontWeight: 700, color: VBS.ink }}>
              {priceText(lineTotal, currency, quoteLabel)}
            </Box>
          </Box>
          <Box sx={{ display: "grid", gap: 1.25 }}>
            <Box
              component="button"
              type="button"
              onClick={() => addProductToCart(false)}
              sx={{ ...btnPrimary, width: "100%" }}
            >
              {canOrder ? t("product.addToCart", "Add to cart") : t("product.signInToOrder", "Sign in to order")}
            </Box>
            {canOrder ? (
              <Box
                component="button"
                type="button"
                onClick={() => addProductToCart(true)}
                sx={{ ...btnSecondary, width: "100%" }}
              >
                {t("product.buyNow", "Buy now")}
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box component="p" sx={{ mt: 2, color: VBS.muted, fontSize: "0.9375rem" }}>
          {t("product.contactPricing", "Contact us for pricing on this item.")}
        </Box>
      )}
    </Box>
  );

  return (
    <StorefrontLayout maxWidth={null}>
      <Box sx={{ bgcolor: VBS.panel, borderBottom: `1px solid ${VBS.lineStrong}`, py: 1.5 }}>
        <Container maxWidth="xl">
          <Box
            component="button"
            type="button"
            onClick={() => navigate(toPath("/catalog"))}
            sx={{
              ...resetButton,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.8125rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: VBS.muted,
              "&:hover": { color: VBS.accent },
            }}
          >
            <VbsIcon name="back" size={14} color="currentColor" />
            {t("product.backToProducts", "Back to products")}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                bgcolor: VBS.surface,
                border: `1px solid ${VBS.lineStrong}`,
                borderRadius: `${VBS.radiusMd}px`,
                overflow: "hidden",
                boxShadow: VBS.shadowCard,
              }}
            >
              <Box
                sx={{
                  aspectRatio: "4 / 3",
                  bgcolor: VBS.imageBg,
                  display: "grid",
                  placeItems: "center",
                  borderBottom: `1px solid ${VBS.line}`,
                }}
              >
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt={product.name}
                    sx={{ width: "100%", height: "100%", objectFit: "contain", p: 2 }}
                  />
                ) : null}
              </Box>
              {images.length > 1 ? (
                <Box sx={{ p: 1.5, display: "flex", gap: 1, overflowX: "auto" }}>
                  {images.map((img, index) => {
                    const url = mediaUrl(img);
                    const active = index === selectedImageIndex;
                    return (
                      <Box
                        key={index}
                        component="button"
                        type="button"
                        onClick={() => setSelectedImageIndex(index)}
                        sx={{
                          ...resetButton,
                          width: 64,
                          height: 64,
                          flexShrink: 0,
                          borderRadius: `${VBS.radius}px`,
                          overflow: "hidden",
                          border: active ? `2px solid ${VBS.accent}` : `1px solid ${VBS.lineStrong}`,
                          bgcolor: VBS.imageBg,
                        }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt=""
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
            </Box>

            {story ? (
              <Box sx={{ mt: 3, bgcolor: VBS.surface, border: `1px solid ${VBS.lineStrong}`, borderRadius: `${VBS.radiusMd}px`, p: 2.5 }}>
                <Box sx={{ ...labelCaps, mb: 1.25 }} component="p">
                  {t("product.details", "Details")}
                </Box>
                <HtmlOrText value={story} />
              </Box>
            ) : null}

            {unitDetails ? (
              <Box sx={{ mt: 2, bgcolor: VBS.surface, border: `1px solid ${VBS.lineStrong}`, borderRadius: `${VBS.radiusMd}px`, p: 2.5 }}>
                <Box sx={{ ...labelCaps, mb: 1.25 }} component="p">
                  {t("product.unitDetails", "Unit details")}
                </Box>
                <HtmlOrText value={unitDetails} />
              </Box>
            ) : null}

            {specEntries.length ? (
              <Box sx={{ mt: 2, bgcolor: VBS.surface, border: `1px solid ${VBS.lineStrong}`, borderRadius: `${VBS.radiusMd}px`, p: 2.5 }}>
                <Box sx={{ ...labelCaps, mb: 1.25 }} component="p">
                  {t("product.specifications", "Specifications")}
                </Box>
                {specEntries.map(([key, val]) => (
                  <Box
                    key={key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.4fr",
                      gap: 2,
                      py: 1,
                      borderBottom: `1px solid ${VBS.line}`,
                      fontSize: "0.875rem",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 600, color: VBS.muted }}>
                      {key}
                    </Box>
                    <Box component="span" sx={{ color: VBS.ink, fontFamily: VBS.fontMono, fontSize: "0.8125rem" }}>
                      {val}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}

            {installationNotes ? (
              <Box sx={{ mt: 2, bgcolor: VBS.surface, border: `1px solid ${VBS.lineStrong}`, borderRadius: `${VBS.radiusMd}px`, p: 2.5 }}>
                <Box sx={{ ...labelCaps, mb: 1.25 }} component="p">
                  {t("product.installationNotes", "Installation notes")}
                </Box>
                <HtmlOrText value={installationNotes} />
              </Box>
            ) : null}

            {technicalSections.length ? (
              <Box sx={{ mt: 2, bgcolor: VBS.surface, border: `1px solid ${VBS.lineStrong}`, borderRadius: `${VBS.radiusMd}px`, p: 2.5 }}>
                <Box sx={{ ...labelCaps, mb: 1.25 }} component="p">
                  {t("product.technicalDetails", "Product details")}
                </Box>
                <Box sx={{ display: "grid", gap: 2 }}>
                  {technicalSections.map((section, index) => (
                    <Box
                      key={`${section.title || "section"}-${index}`}
                      sx={{ bgcolor: VBS.panel, borderRadius: `${VBS.radius}px`, p: 2 }}
                    >
                      {section.title ? (
                        <Box
                          component="h3"
                          sx={{ m: "0 0 0.75rem", fontSize: "1rem", fontWeight: 800, color: VBS.ink }}
                        >
                          {section.title}
                        </Box>
                      ) : null}
                      {section.body ? <HtmlOrText value={section.body} /> : null}
                      {section.images.length ? (
                        <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1.25 }}>
                          {section.images.map((img, imageIndex) => {
                            const url = mediaUrl(img);
                            if (!url) return null;
                            return (
                              <Box
                                key={`${index}-${imageIndex}`}
                                component="img"
                                src={url}
                                alt={section.title || t("product.technicalDetails", "Product details")}
                                sx={{
                                  width: "100%",
                                  aspectRatio: "1 / 1",
                                  objectFit: "cover",
                                  borderRadius: `${VBS.radius}px`,
                                  bgcolor: VBS.imageBg,
                                }}
                              />
                            );
                          })}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              component="span"
              sx={{ fontFamily: VBS.fontMono, fontSize: "0.75rem", color: VBS.subtle }}
            >
              SKU {product.sku || "TBD"}
            </Box>
            <Box
              component="h1"
              sx={{
                m: "0.35rem 0 0",
                fontSize: { xs: "1.625rem", md: "2rem" },
                fontWeight: 800,
                lineHeight: 1.15,
                color: VBS.ink,
              }}
            >
              {product.name}
            </Box>

            {(transmissions.length || fitmentYears) ? (
              <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {fitmentYears ? (
                  <Box
                    sx={{
                      px: 1.1,
                      py: 0.45,
                      borderRadius: `${VBS.radius}px`,
                      bgcolor: VBS.accentSoft,
                      color: VBS.accent,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {t("product.fitmentYears", "Fitment")}: {fitmentYears}
                  </Box>
                ) : null}
                {transmissions.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      px: 1.1,
                      py: 0.45,
                      borderRadius: `${VBS.radius}px`,
                      border: `1px solid ${VBS.lineStrong}`,
                      bgcolor: VBS.surface,
                      color: VBS.ink,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            ) : null}

            <Box sx={{ mt: 2.5 }}>{buyPanel}</Box>
          </Grid>
        </Grid>
      </Container>
    </StorefrontLayout>
  );
}
